<template>
  <div class="es-tooltip">
    {{ text }}
    <div
      class="es-tooltip-arrow"
      data-popper-arrow
    />
  </div>
</template>

<script>
import { computePosition, offset, flip, shift, arrow } from '@floating-ui/dom'

export default {
  name: 'ElTooltip',
  props: {
    relateTo: {
      default: null
    },
    text: {
      type: String,
      default: 'My Tooltip'
    },
    placement: {
      type: String,
      default: 'top'
    },
    enable: {
      default: false
    },
    smallElement: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      middlewareChain: [],
      cleanupFn: null,
      arrowRef: null
    }
  },
  methods: {
    async init () {
      const referenceEl = this.relateTo || this.$el.parentElement
      const floatingEl = this.$el
      this.arrowRef = this.$el.querySelector('.es-tooltip-arrow')

      this.middlewareChain = [
        offset(8),
        flip(),
        shift(),
        arrow({ element: this.arrowRef })
      ]

      const update = async () => {
        const { x, y, placement, middlewareData } = await computePosition(referenceEl, floatingEl, {
          placement: this.placement,
          middleware: this.middlewareChain
        })

        Object.assign(floatingEl.style, {
          left: `${x}px`,
          top: `${y}px`,
          position: 'absolute'
        })

        floatingEl.setAttribute('data-popper-placement', placement)

        const arrowData = middlewareData.arrow
        if (arrowData && this.arrowRef) {
          const side = placement.split('-')[0]
          const alignment = placement.split('-')[1] || ''
          const staticSide = {
            top: 'bottom',
            bottom: 'top',
            left: 'right',
            right: 'left'
          }[side] || 'top'

          const offsetAdjust = 8

          let arrowOffset = '-4px'

          if (this.smallElement && side === 'left') {
            arrowOffset = '0'
          } else if (this.smallElement && side === 'right') {
            arrowOffset = '-8px'
          }

          Object.assign(this.arrowRef.style, {
            left: arrowData.x != null
              ? `${arrowData.x + (alignment === 'end' && ['top', 'bottom'].includes(side) ? offsetAdjust : alignment === 'start' && ['top', 'bottom'].includes(side) ? -offsetAdjust : 0)}px`
              : '',
            top: arrowData.y != null
              ? `${arrowData.y + (alignment === 'end' && ['left', 'right'].includes(side) ? offsetAdjust : alignment === 'start' && ['left', 'right'].includes(side) ? -offsetAdjust : 0)}px`
              : '',
            right: '',
            bottom: '',
            [staticSide]: arrowOffset
          })
        }
      }

      this.cleanupFn = () => {
        referenceEl.removeEventListener('mouseenter', this.show)
        referenceEl.removeEventListener('mouseleave', this.hide)
        referenceEl.removeEventListener('focus', this.show)
        referenceEl.removeEventListener('blur', this.hide)
      }

      referenceEl.addEventListener('mouseenter', this.show)
      referenceEl.addEventListener('mouseleave', this.hide)
      referenceEl.addEventListener('focus', this.show)
      referenceEl.addEventListener('blur', this.hide)

      this.updatePosition = update
      await update()
    },
    async show () {
      if (!this.enable) return
      this.$el.setAttribute('data-show', '')
      await this.updatePosition()
    },
    hide () {
      if (!this.enable) return
      this.$el.removeAttribute('data-show')
    }
  },
  watch: {
    placement () {
      this.init()
    },
    enable (val) {
      if (val) {
        this.init()
      } else {
        this.hide()
        if (this.cleanupFn) this.cleanupFn()
      }
    }
  },
  mounted () {
    if (this.enable) this.init()
  }
}
</script>

<style lang="scss">
.es-tooltip {
  background: #333;
  color: white;
  font-weight: bold;
  padding: 4px 8px;
  font-size: 13px;
  border-radius: 4px;
  display: none;
  position: absolute;
  z-index: 100;
}

.es-tooltip[data-show] {
  display: inline-block;
}

.es-tooltip-arrow,
.es-tooltip-arrow::before {
  position: absolute;
  width: 8px;
  height: 8px;
  background: inherit;
}

.es-tooltip-arrow {
  visibility: hidden;
}

.es-tooltip-arrow::before {
  visibility: visible;
  content: '';
  transform: rotate(45deg);
}
</style>